import React, { useState, useContext, useEffect } from "react";
import { Album, Event, Photo } from "./types";

// @ts-ignore
import Gallery from "react-grid-gallery";
import _ from "lodash";
// @ts-ignore
import ahoy from "ahoy.js";
import LightBox from "./LightBox";
import { EventGalleryContext } from "./index";
import { FindPhotosGalleryContext } from "../MasterImageSearch/components/results/PhotosContainer";
const playIcon = require("./styles/play.svg");
const hideIcon = require("./styles/imgHide.svg");
import Telemetry from "../../utils/rudderstackAnalytics";
import { OrgDownloadContext } from "../OrganizationPhotos/OrgDownloadContext";

import {
  CartOutline,
  CartFilled
} from "../BrowseEvent/components/vectors/AddToCartIcon";
import {
  CheckOutline,
  CheckFilled
} from "../BrowseEvent/components/vectors/CheckIcon";

const MobilePhotoGallery = ({
  photos,
  trackTelemetry,
  isAuthorizedForDirectDownloads,
  userId,
  reset,
  primaryColor,
  parentName,
  showFavIcon,
  canShowGuestEmailModal,
  securedOrgView,
  registerGuestUserAPI,
  setGuestUserId,
  eventClickable,
  disableSelectIcon,
  secureAccessToken
}: {
  photos: Photo[];
  trackTelemetry: boolean;
  isAuthorizedForDirectDownloads: boolean;
  userId?: number;
  reset?: boolean;
  primaryColor?: string;
  parentName?: string;
  showFavIcon?: boolean;
  canShowGuestEmailModal: (photo: any) => void;
  securedOrgView?: boolean;
  registerGuestUserAPI: string;
  setGuestUserId: (userId: number) => void;
  eventClickable?: boolean;
  disableSelectIcon?: boolean;
  secureAccessToken?: string;
}) => {
  const [photoIndex, setPhotoIndex] = useState(-1);
  const [curationApiCalled, setCurationApiCalled] = useState(false);
  const [currentMedia, setCurrentMedia] = useState<{
    mediaId: number;
    currentState: boolean;
  }>();
  const [showLightBox, setShowLightBox] = useState(false);
  const [photosState, setphotos] = useState<Photo[]>([]);
  const context = useContext(EventGalleryContext);
  const findPhotosContext = useContext(FindPhotosGalleryContext);
  const orgDownloadContext = useContext(OrgDownloadContext);

  useEffect(() => {
    if (reset) {
      setShowLightBox(false), setPhotoIndex(-1);
    }
    setphotos(photos);
  }, [photos]);

  interface ImageComponentProps {
    props: {
      index: number;
      item: {
        videoContent: any;
        videoDuration: string | number;
        thumbnail: string | undefined;
        id: number;
        album: Album;
        hidden: boolean;
        isSelected: boolean;
      };
      imageProps: JSX.IntrinsicAttributes &
        React.ClassAttributes<HTMLImageElement> &
        React.ImgHTMLAttributes<HTMLImageElement>;
    };
    handleImageSelect: ({
      photo,
      index
    }: {
      photo: any;
      index: number;
    }) => void;
    primaryColor: string;
    shouldShowCheckIcon: boolean;
    shouldShowDownloadSelect: () => boolean;
    directDownloadEnabled: boolean | undefined;
    handleDownloadImageSelection: ({
      photo,
      index
    }: {
      photo: any;
      index: number;
    }) => void;
  }

  const renderIcon = (
    props: any,
    shouldShowCheckIcon: boolean,
    primaryColor: string
  ) => {
    const isSelectedFromContext =
      orgDownloadContext?.downloadableContent?.includes(props.item.id) ?? false;

    const isSelected = orgDownloadContext
      ? isSelectedFromContext
      : props.item.isSelected;

    // Check if this is a print products event with the new flow enabled
    const isPrintProductsNewFlow = context?.printProductsflow?.();

    if (shouldShowCheckIcon && directDownloadEnabled) {
      return isSelected ? (
        <CheckFilled color={primaryColor} />
      ) : (
        <CheckOutline />
      );
    } else if (isPrintProductsNewFlow) {
      // For print products with new flow, don't show cart icons
      return null;
    } else {
      return isSelected ? <CartFilled color={primaryColor} /> : <CartOutline />;
    }
  };

  const ImageComponent = ({
    props,
    handleImageSelect,
    primaryColor,
    shouldShowCheckIcon,
    handleDownloadImageSelection,
    shouldShowDownloadSelect,
    directDownloadEnabled
  }: ImageComponentProps) => {
    const isPrintProductsNewFlow = context?.printProductsflow?.();
    
    const ImageSelectIcon = (
      <div
        className="geo-cart-icon-container"
        onClick={e => {
          e.stopPropagation();
          // Don't handle clicks for print products with new flow
          if (isPrintProductsNewFlow) {
            return;
          }
          shouldShowDownloadSelect()
            ? handleDownloadImageSelection({
                photo: props.item,
                index: props.index
              })
            : handleImageSelect({ photo: props.item, index: props.index });
        }}
        style={{
          cursor: isPrintProductsNewFlow ? "default" : "pointer"
        }}
      >
        {renderIcon(props, shouldShowCheckIcon, primaryColor)}
      </div>
    );

    const PaidMediaIcon = (
      <div className="geo-cart-icon-container paid-media-symbol-container">
        <span>$</span>
      </div>
    );

    const showPaidMediaIcon =
      directDownloadEnabled &&
      orgDownloadContext?.fromSecuredFaceSearch &&
      props.item.album?.event?.price !== 0;

    return (
      <div style={{ textAlign: "center", cursor: "pointer" }}>
        {props.item.hidden && (
          <span className="visibility-stamp">
            <img src={hideIcon} />
          </span>
        )}
        {props.item.videoContent && (
          <img
            className="play-icon"
            src={playIcon}
            alt={`${props.item.album.name}-${props.item.id}`}
          />
        )}
        {showPaidMediaIcon ? PaidMediaIcon : ImageSelectIcon}
        <img
          {...props.imageProps}
          alt={`${props.item.album.name}-${props.item.id}`}
        />
      </div>
    );
  };

  const directDownloadEnabled =
    context?.directDownloadEnabled ||
    orgDownloadContext?.directDownloadEnabled ||
    findPhotosContext?.directDownloadEnabled ||
    false;

  const updatePhotoInCart = (photo: any) => {
    if (photo.isSelected) {
      if (trackTelemetry) {
        Telemetry.track("event-gallery:remove-from-cart", {
          eventId: context.event?.id,
          eventName: context.event?.name,
          photoId: photo.id,
          cartId: context.cart?.id,
          source: "thumbnail",
          embeddedMode: context.isEmbed
        });
      }
      if (context.cart) context.removePhotoFromCart(photo);
      else findPhotosContext.removePhotoFromCart(photo);
    } else {
      if (trackTelemetry) {
        Telemetry.track("event-gallery:add-to-cart", {
          eventId: context.event?.id,
          eventName: context.event?.name,
          photoId: photo.id,
          cartId: context.cart?.id,
          source: "thumbnail",
          embeddedMode: context.isEmbed
        });
      }
      if (context.canShowEmailPopup()) {
        canShowGuestEmailModal(photo);
      } else if (context.event) {
        // considering this context is from MobileEventGallery
        // Needs to be Refactored
        context.addPhotoToCart(photo, true);
      } else {
        findPhotosContext.addPhotoToCart(photo);
      }
    }
  };

  useEffect(() => {
    if (photoIndex >= 0) {
      setShowLightBox(true);
    }
  }, [photoIndex]);

  const toggleLightBox = (index: number) => {
    if (photos[photoIndex] && photos[photoIndex].videoContent) {
      setShowLightBox(false);
    }
    setPhotoIndex(index);
    if (index == -1) {
      setShowLightBox(false);
    }
    if (index >= 0) {
      if (!context.guestUserId) {
        setCurrentMedia({
          mediaId: photos[index].id,
          currentState: photos[index].favorite
        });
      }
      if (trackTelemetry) {
        ahoy.track("albumInteraction", {
          secondary_type: "openedPhotoLightbox",
          album_id: photos[index].album.id,
          event_id: photos[index].album.eventId
        });
        Telemetry.track("event-gallery:opened-lightbox", {
          eventId: context.event.id,
          eventName: context.event.name,
          photoId: photos[index].id,
          embeddedMode: context.isEmbed
        });
      }
    }
  };

  const contextMenu = (e: any) => {
    e.preventDefault();
    return false;
  };

  const tagStyle = {
    display: "inline",
    padding: ".2em .6em .3em",
    fontSize: "60%",
    fontWeight: "600",
    lineHeight: "1",
    color: "#FFF",
    background: "rgba(7,36,22,.6)",
    textAlign: "center",
    whiteSpace: "nowrap",
    verticalAlign: "baseline",
    borderRadius: ".25em"
  };

  const handleImageSelect = ({
    photo,
    index
  }: {
    photo: any;
    index: number;
  }) => {
    context.curationMode
      ? //@ts-ignore
        (setphotos(context.getPhotoId(photo, index, photos)),
        context.getPhotoId(photo, index, photos))
      : updatePhotoInCart(photo);
  };
  const handleDownloadImageSelection = ({
    photo,
    index
  }: {
    photo: any;
    index: number;
  }) => {
    if (orgDownloadContext) {
      const isPhotoInDownloadList =
        orgDownloadContext.downloadableContent?.includes(photo.id);

      if (isPhotoInDownloadList)
        orgDownloadContext.removeContentFromDownload(photo);
      else orgDownloadContext.addContentToDownload(photo);
    } else {
      context.getPhotoId(photo, index, photos);
    }
  };

  const shouldShowDownload = () => {
    return context?.event?.price === 0 && directDownloadEnabled;
  };

  const shouldShowDownloadSelect = () => {
    return (
      (!context?.curationMode &&
        context?.event?.price === 0 &&
        directDownloadEnabled) ||
      (!!orgDownloadContext && directDownloadEnabled)
    );
  };

  const shouldShowCheckIcon = () => {
    return (
      !disableSelectIcon &&
      (securedOrgView || shouldShowDownload() || context?.curationMode)
    );
  };

  return (
    <div className="dv-album-wrap">
      <Gallery
        images={photosState && photosState.length > 0 ? photosState : photos}
        rowHeight={120}
        margin={5}
        enableLightbox={false}
        enableImageSelection={false}
        onClickThumbnail={(index: number) => {
          toggleLightBox(index);
        }}
        tagStyle={tagStyle}
        setGuestUserId={setGuestUserId}
        tileViewportStyle={customThumbViewportStyle}
        thumbnailStyle={customThumbStyle}
        selectedIconColor={
          primaryColor
            ? primaryColor
            : context.pageConfiguration
            ? context.pageConfiguration.primaryColor
            : "#259FA5"
        }
        enableKeyboardInput={true}
        onContextMenu={contextMenu}
        onRender={() => null}
        thumbnailImageComponent={(props: any) => {
          return (
            <ImageComponent
              props={props}
              handleImageSelect={handleImageSelect}
              primaryColor={
                primaryColor
                  ? primaryColor
                  : context.pageConfiguration
                  ? context.pageConfiguration.primaryColor
                  : "#259FA5"
              }
              shouldShowCheckIcon={shouldShowCheckIcon()}
              handleDownloadImageSelection={handleDownloadImageSelection}
              shouldShowDownloadSelect={shouldShowDownloadSelect}
              directDownloadEnabled={directDownloadEnabled}
            />
          );
        }}
      />
      {showLightBox && (
        <LightBox
          photos={photosState && photosState.length > 0 ? photosState : photos}
          photoIndex={photoIndex}
          toggleLightBox={toggleLightBox}
          trackTelemetry={trackTelemetry}
          isAuthorizedForDirectDownloads={isAuthorizedForDirectDownloads}
          userId={userId}
          primaryColor={primaryColor}
          curationApiCalled={curationApiCalled}
          setCurationApiCalled={(val: boolean) => setCurationApiCalled(val)}
          parentName={parentName}
          showFavIcon={showFavIcon}
          canShowGuestEmailModal={canShowGuestEmailModal}
          securedOrgView={securedOrgView}
          registerGuestUserAPI={registerGuestUserAPI}
          setGuestUserId={setGuestUserId}
          eventClickable={eventClickable}
          secureAccessToken={secureAccessToken}
        />
      )}
    </div>
  );
};

const customThumbViewportStyle = function () {
  // @ts-ignore
  const item = this.props.item;
  return {
    width: item.vwidth,
    minWidth: 60,
    height: item.height,
    overflow: "hidden",
    position: "initial"
  };
};

const customThumbStyle = function () {
  // @ts-ignore
  const item = this.props.item;
  return {
    cursor: "pointer",
    width: item.scaletwidth,
    height: item.height,
    marginLeft: item.marginLeft,
    marginTop: 0,
    WebkitTouchCallout: "none"
  };
};

export default MobilePhotoGallery;
